/**
 * Pure mapping from the API's session response to what the shell consumes. Kept apart from
 * the provider so it can be unit-tested without React.
 */
import {
  formatPersonName,
  formatSiteName,
  type AccessScope,
  type SessionResponse,
  type ViewingAs,
} from '@aroo/shared';
import { accessFromEffective, type Access } from './access';
import type { ScopeChoice, SessionUser } from './session';

export const NO_SCOPE: ScopeChoice = { id: 'none', label: 'Aroo', kind: 'all', group: true };

/** The switcher shows the engine's label: the place, or "Acting for <seat>" for a cover. */
export function scopeChoiceOf(scope: AccessScope): ScopeChoice {
  const id = `${scope.kind}:${scope.id ?? 'all'}`;
  const label = scope.label || scope.name;
  switch (scope.kind) {
    case 'company':
      return { id, label, kind: 'all', group: true };
    case 'division':
      return { id, label, kind: 'group', group: true };
    case 'region':
      return { id, label, kind: 'region', group: true };
    default:
      return { id, label: formatSiteName(label), kind: 'branch' };
  }
}

/**
 * Broad scopes first, each region or division followed by its branches (indented), then any
 * branch held on its own that isn't already listed under a region.
 */
export function scopeChoicesOf(scopes: readonly AccessScope[]): ScopeChoice[] {
  const choices: ScopeChoice[] = [];
  const seen = new Set<string>();
  const add = (c: ScopeChoice) => {
    if (seen.has(c.id)) return;
    seen.add(c.id);
    choices.push(c);
  };
  for (const scope of scopes) {
    if (scope.kind === 'site') continue;
    const parent = scopeChoiceOf(scope);
    add(parent);
    if (scope.regions && scope.regions.length > 0) {
      // Company or division: its regions, each with its branches (two levels in).
      for (const region of scope.regions) {
        const regionId = `region:${region.id ?? ''}`;
        add({ id: regionId, label: region.name, kind: 'region', group: true, within: parent.id });
        for (const site of region.sites) {
          add({
            id: `site:${site.id ?? ''}`,
            label: formatSiteName(site.name),
            kind: 'branch',
            within: regionId,
            depth: 2,
          });
        }
      }
      continue;
    }
    for (const site of scope.sites ?? []) {
      add({
        id: `site:${site.id ?? ''}`,
        label: formatSiteName(site.name),
        kind: 'branch',
        within: parent.id,
      });
    }
  }
  for (const scope of scopes) if (scope.kind === 'site') add(scopeChoiceOf(scope));
  return choices.length > 0 ? choices : [NO_SCOPE];
}

/** The scope a switcher choice stands for, as pages filter by it (null: no real scope). */
export function scopeRefOfChoice(
  choice: ScopeChoice,
): { kind: 'site' | 'region' | 'division' | 'company'; id: string | null } | null {
  const [kind, id] = choice.id.split(':') as [string, string | undefined];
  if (kind === 'company') return { kind, id: null };
  if ((kind === 'site' || kind === 'region' || kind === 'division') && id) return { kind, id };
  return null;
}

/**
 * The role that grants the most permissions through company-wide grants (not seats): what an
 * admin account with no seat is in Aroo ("Aroo Admin"), rather than its staff job title. A
 * narrow grant (one site's fleet, say) doesn't replace the job title.
 */
export function topGrantedRole(session: SessionResponse): string | null {
  const counts = new Map<string, number>();
  for (const p of session.access.permissions) {
    const roles = new Set(
      p.sources
        .filter((s) => s.kind === 'grant' && s.scope.kind === 'company')
        .map((s) => s.roleName),
    );
    for (const r of roles) counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  let best: string | null = null;
  let most = 0;
  for (const [role, n] of counts) {
    if (n > most) [best, most] = [role, n];
  }
  return best;
}

export function userOf(session: SessionResponse): SessionUser {
  const { me } = session;
  const firstName = formatPersonName(me.preferredName?.trim() || me.givenName);
  const position = me.positions[0];
  return {
    id: me.personId,
    firstName,
    name: formatPersonName(me.displayName),
    initials: me.initials,
    homeBranch: formatSiteName(me.homeSite?.name ?? session.access.scopes[0]?.name ?? ''),
    homeSiteCode: me.homeSite?.code ?? null,
    // The seat held first; else the role granted (an admin account); the job title last.
    roleTitle:
      position?.title ??
      topGrantedRole(session) ??
      me.jobTitle ??
      (me.kind === 'external' ? 'External' : 'Staff'),
  };
}

export function accessOf(session: SessionResponse): Access {
  const access = accessFromEffective(session.access);
  // Fleet M2 (D65): Fleet shows only when it is relevant to the person (the API decides).
  if (session.fleetRelevant === false) {
    return { ...access, sections: access.sections.filter((s) => s !== 'fleet') };
  }
  return access;
}

export function viewingAsOf(session: SessionResponse): ViewingAs | null {
  return session.viewingAs ?? null;
}

export function approverNameOf(session: SessionResponse): string {
  const lm = session.me.lineManager;
  return lm?.name ?? lm?.title ?? 'your manager';
}
